import { FC, FocusEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormErrorMessage,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Country, DonationField } from '../../constants';
import { DonationFormLabel } from '../donationFormLabel';
import { SelectState } from '../selectField';

import { Address, AddressAutoComplete } from '../addressAutoComplete';
import { MaskedInput } from '../maskedInput';

export interface BillingAddressProps {
  form?: any;
}

export const BillingAddress: FC<BillingAddressProps> = () => {
  const { control, clearErrors, setValue, watch } = useFormContext();
  const isUSCan =
    watch(DonationField.country) === Country.UnitedStates ||
    watch(DonationField.country) === Country.Canada;
  const isUS = watch(DonationField.country) === Country.UnitedStates;
  const isCan = watch(DonationField.country) === Country.Canada;
  const StateTag = isUSCan ? SelectState : Input;

  /**
   * @description The controller pattern is a little verbose, so reduce the noise with a helper.
   *
   */
  const controllerInputHelper = ({
    isRequired = true,
    fieldLabel,
    fieldName,
    maxLength,
  }: {
    isRequired?: boolean;
    fieldLabel: string;
    fieldName: string;
    maxLength?: number;
  }) => (
    <Controller
      control={control}
      name={fieldName}
      render={({ field, fieldState }) => (
        <FormControl isInvalid={!!fieldState.error} isRequired={isRequired}>
          <DonationFormLabel htmlFor={fieldName}>
            {fieldLabel}
          </DonationFormLabel>
          <Input
            {...field}
            maxLength={maxLength}
            id={fieldName}
            onChangeCapture={(e) => {
              field.onChange(e.currentTarget.value);
            }}
            onBlurCapture={({ target }) => {
              const trimmedValue = target.value.trim();
              field.onChange(trimmedValue);
              setValue(fieldName, trimmedValue);
            }}
          />
          <FormErrorMessage>{fieldState?.error?.message}</FormErrorMessage>
        </FormControl>
      )}
    />
  );

  const resetErrors = () => {
    clearErrors([
      DonationField.address1,
      DonationField.city,
      DonationField.postCode,
      DonationField.state,
    ]);
  };

  return (
    <VStack spacing={10} w="full">
      <VStack align="left" spacing={6} w="full">
        <Text as="h3" fontSize="1.5rem">
          Billing Address
        </Text>

        <Controller
          control={control}
          name={DonationField.address1}
          render={({ field, fieldState }) => (
            <FormControl isRequired isInvalid={!!fieldState.error}>
              <DonationFormLabel htmlFor={DonationField.address1}>
                Address 1
              </DonationFormLabel>

              {isUSCan && (
                <AddressAutoComplete
                  value={field.value}
                  name={field.name}
                  onChange={field.onChange}
                  onBlur={({ currentTarget }: FocusEvent<HTMLInputElement>) => {
                    const trimmedValue = currentTarget.value.trim();
                    field.onChange(trimmedValue);
                    field.onBlur();
                  }}
                  onAddressSelected={(suggestedAddress: Address) => {
                    setValue(DonationField.address1, suggestedAddress.address1);
                    setValue(DonationField.address2, suggestedAddress.address2);
                    setValue(DonationField.city, suggestedAddress.city);
                    setValue(DonationField.state, suggestedAddress.state);
                    setValue(DonationField.postCode, suggestedAddress.postCode);
                    resetErrors();
                  }}
                />
              )}

              {!isUSCan && (
                <Input
                  maxLength={50}
                  onChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  onBlurCapture={({ target }) => {
                    const trimmedAddress = target.value.trim();
                    if (trimmedAddress !== '') {
                      setValue(DonationField.address1, trimmedAddress);
                    }
                  }}
                />
              )}
              <FormErrorMessage>{fieldState?.error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />

        {controllerInputHelper({
          isRequired: false,
          fieldName: DonationField.address2,
          fieldLabel: 'Address 2',
          maxLength: isUSCan ? 30 : 50,
        })}
        {!isUSCan &&
          controllerInputHelper({
            isRequired: false,
            fieldName: DonationField.address3,
            fieldLabel: 'Address 3',
            maxLength: 50,
          })}
        {controllerInputHelper({
          fieldName: DonationField.city,
          fieldLabel: 'City',
          maxLength: isUSCan ? 30 : 50,
        })}

        <Controller
          control={control}
          name={DonationField.state}
          render={({ field, fieldState }) => (
            <FormControl isRequired isInvalid={!!fieldState.error}>
              <DonationFormLabel htmlFor={DonationField.state}>
                {isUS ? 'State' : isCan ? 'Province' : 'State/Province/Region'}
              </DonationFormLabel>
              <StateTag
                onChange={field.onChange}
                value={field.value}
                name={field.name}
                onBlurCapture={({ target }) => {
                  const trimmedState = target.value.trim();
                  if (trimmedState !== '') {
                    setValue(DonationField.state, trimmedState);
                  }
                }}
                onBlur={field.onBlur}
                id={DonationField.state}
                maxLength={50}
              />
              <FormErrorMessage>{fieldState?.error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name={DonationField.postCode}
          render={({ field, fieldState }) => (
            <FormControl isRequired isInvalid={!!fieldState.error}>
              <DonationFormLabel htmlFor={DonationField.postCode}>
                {isUS ? 'Zip' : 'Postal Code'}
              </DonationFormLabel>
              {isUS && (
                <MaskedInput
                  {...field}
                  mask="_____"
                  replacement={{ _: /\d/ }}
                  aria-label={DonationField.postCode}
                  id={DonationField.postCode}
                  onBlur={({ target }) => {
                    field.onBlur();
                    const trimmedPostCode = target.value.trim();
                    if (trimmedPostCode !== '') {
                      setValue(DonationField.postCode, trimmedPostCode);
                    }
                  }}
                />
              )}
              {isCan && (
                <MaskedInput
                  {...field}
                  mask="ANA NAN"
                  replacement={{ A: /[A-Za-z]/, N: /\d/ }}
                  id={DonationField.postCode}
                  onBlur={({ target }) => {
                    field.onBlur();
                    const trimmedPostCode = target.value.trim();
                    if (trimmedPostCode !== '') {
                      setValue(DonationField.postCode, trimmedPostCode);
                    }
                  }}
                />
              )}
              {!isUSCan && (
                <Input
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  maxLength={20}
                  onBlurCapture={({ target }) => {
                    field.onBlur();
                    const trimmedPostCode = target.value.trim();
                    if (trimmedPostCode !== '') {
                      setValue(DonationField.postCode, trimmedPostCode);
                    }
                  }}
                />
              )}
              <FormErrorMessage>{fieldState?.error?.message}</FormErrorMessage>
            </FormControl>
          )}
        />
      </VStack>
    </VStack>
  );
};
